import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, BookOpen, Globe, FlaskConical, GraduationCap } from "lucide-react";
import { fetchResources } from "../lib/api";
import PageHero from "../components/PageHero";

const CATS = [
  { key: "all", icon: BookOpen },
  { key: "courses", icon: GraduationCap },
  { key: "platforms", icon: Globe },
  { key: "research", icon: FlaskConical },
  { key: "guides", icon: BookOpen },
];

export default function KnowledgeHub() {
  const { t } = useTranslation();
  const [resources, setResources] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const loading = resources === null;

  useEffect(() => {
    let active = true;
    fetchResources({})
      .then((data) => { if (active) setResources(data); })
      .catch(() => { if (active) setResources([]); });
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    return (resources || []).filter((r) => {
      const inCat = cat === "all" || r.category === cat;
      const ql = q.trim().toLowerCase();
      const inQ = !ql || r.title.toLowerCase().includes(ql) || r.description.toLowerCase().includes(ql) || r.tags?.some((tg) => tg.toLowerCase().includes(ql));
      return inCat && inQ;
    });
  }, [resources, cat, q]);

  return (
    <div data-testid="page-knowledge-hub">
      <PageHero
        testId="knowledge-hero"
        kicker={t("knowledge.kicker")}
        title={t("knowledge.headline")}
        lead={t("knowledge.lead")}
        aside={
          <div className="paper p-6 lg:p-8 lg:mt-24">
            <div className="overline text-gold">{t("knowledge.kicker")}</div>
            <div className="mt-4 flex items-center gap-3 border-b border-line focus-within:border-gold">
              <Search className="h-4 w-4 text-slate" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("knowledge.search_placeholder")}
                className="gxr-input !border-none !py-2"
                data-testid="knowledge-search-input"
              />
            </div>
          </div>
        }
      />

      <section className="bg-ivory/95 backdrop-blur-md border-b border-line sticky top-[76px] z-30" data-testid="knowledge-filters">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-4 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-2 min-w-max">
            {CATS.map(({ key, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setCat(key)}
                data-testid={`knowledge-filter-${key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-semibold transition-all ${
                  cat === key ? "bg-forest text-white border-forest" : "bg-white text-ink/70 border-line hover:border-gold hover:text-forest"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t(`knowledge.filters.${key}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-1" data-testid="knowledge-grid-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          {loading ? (
            <div className="text-center text-slate py-20" data-testid="knowledge-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate py-20 font-display text-2xl" data-testid="knowledge-empty">{t("knowledge.empty")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="knowledge-grid">
              {filtered.map((r, i) => (
                <motion.a
                  key={r.id}
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                  className="group p-7 gxr-card flex flex-col"
                  data-testid={`knowledge-resource-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="pill">{t(`knowledge.filters.${r.category}`)}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform rtl:-scale-x-100" />
                  </div>
                  <h3 className="font-display text-2xl mt-5 leading-snug text-ink group-hover:text-forest transition-colors">{r.title}</h3>
                  <div className="mt-1 text-xs uppercase tracking-[0.14em] text-slate">{r.provider}</div>
                  <p className="mt-4 text-sm text-slate leading-relaxed">{r.description}</p>
                  <div className="mt-auto pt-6 flex flex-wrap gap-1.5">
                    {r.tags?.slice(0, 4).map((tg) => (
                      <span key={tg} className="text-[10px] uppercase tracking-[0.12em] text-forest bg-ivory border border-line rounded-md px-2 py-1">{tg}</span>
                    ))}
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
